import {
  faSearch,
  faSpinner,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Formik } from "formik";
import React from "react";
import {
  Form,
  FormControl,
  FormGroup,
  InputGroup,
  Stack,
} from "react-bootstrap";
import { useSelector } from "react-redux";
import FormConstants from "../../../../shared/constants/Form.constants";
import { ClickEvent } from "../../../../shared/types/event.types";
import { ISearchFormValues } from "../../../../shared/types/Form.types";
import FormUtils from "../../../../shared/utils/Form.utils";
import { RootState } from "../../../../state-management/root-reducer";
import {
  setCurrentPage,
  setOffset,
} from "../../../../state-management/slices/pagination/pagination.slice";
import {
  clearSearchTerm,
  setSearchTerm,
} from "../../../../state-management/slices/search/search.slice";
import { useAppDispatch } from "../../../../state-management/store";
import BUTTON from "../../../button/Button.component";
import SearchStyles from "./Search.styles";
import { DefaultSearchProps, ISearchProps } from "./Search.types";
import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";

/* istanbul ignore next */
const renderIcon = (isFetching: boolean, searchTerm: string | null) => {
  if (!searchTerm) {
    return <FontAwesomeIcon icon={faSearch} color="#666666" />;
  }

  if (isFetching) {
    return (
      <FontAwesomeIcon icon={faSpinner} className="fa-spin" color="#666666" />
    );
  }

  return <FontAwesomeIcon icon={faSearch} color="#666666" />;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SEARCH: React.FC<ISearchProps> = (props: ISearchProps) => {
  const dispatch = useAppDispatch();
  const sendDataToGTM = useGTMDispatch();

  // APP STATE
  const { searchTerm, isFetching } = useSelector(
    (state: RootState) => state.search
  );

  return (
    <SearchStyles.SearchComponent
      className="search-component"
      data-testid="search-component"
    >
      <Formik
        validationSchema={FormUtils.projectSearchSchema}
        initialValues={FormConstants.InitialSearchValues}
        onSubmit={(values: ISearchFormValues) => {
          dispatch(setSearchTerm(values.searchTerm));
          dispatch(setOffset(0));
          dispatch(setCurrentPage(1));
          sendDataToGTM({
            event: "eventTracking",
            category: "Construction",
            action: "Search - View Projects",
            label: `Search term submitted: ${values.searchTerm}`,
          });
        }}
      >
        {({
          handleSubmit,
          handleChange,
          handleBlur,
          values,
          touched,
          isValid,
          errors,
          resetForm,
        }) => (
          <Form
            id="SearchProjectsForm"
            aria-label="search-form"
            noValidate
            onSubmit={handleSubmit}
            autoComplete="off"
          >
            <Stack direction="horizontal">
              <FormGroup className="input-form-group">
                <InputGroup className="search-input-group">
                  <FormControl
                    required
                    aria-label="search-field"
                    type="text"
                    name="searchTerm"
                    className={
                      touched.searchTerm && errors.searchTerm
                        ? "search-input invalid"
                        : "search-input"
                    }
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isValid={touched.searchTerm && !errors.searchTerm}
                    value={values.searchTerm}
                  />
                  <button
                    type="submit"
                    data-testid="submit-search-button"
                    className="search-button"
                    disabled={!isValid}
                    onClick={/* istanbul ignore next */ () => handleSubmit}
                  >
                    {renderIcon(isFetching, searchTerm)}
                  </button>
                </InputGroup>
              </FormGroup>
              {
                /* istanbul ignore next */ searchTerm && (
                  <FormGroup
                    className="clear-button-group"
                    aria-label="clear-search-group"
                  >
                    <BUTTON
                      variant="secondary"
                      className="clear-button blue-theme no-scale"
                      onClick={
                        /* istanbul ignore next */ (event: ClickEvent) => {
                          event.preventDefault();
                          dispatch(clearSearchTerm());
                          dispatch(setOffset(0));
                          dispatch(setCurrentPage(1));
                          sendDataToGTM({
                            event: "eventTracking",
                            category: "Construction",
                            action: "Click - View Projects",
                            label: "Clear Search",
                          });
                          resetForm();
                        }
                      }
                    >
                      <Stack direction="horizontal">
                        <div className="clear-label">Clear</div>
                        <FontAwesomeIcon icon={faTimesCircle} color="#0079C1" />
                      </Stack>
                    </BUTTON>
                  </FormGroup>
                )
              }
            </Stack>
            <Form.Text
              className={`form-text-prompt ${
                /* istanbul ignore next */ !isValid ? "invalid" : ""
              }`}
            >
              <p>
                Search by Project name, Address, Package name, Notification
                number, or Main Contact. Minimum 3 characters
              </p>
            </Form.Text>
          </Form>
        )}
      </Formik>
    </SearchStyles.SearchComponent>
  );
};

SEARCH.defaultProps = DefaultSearchProps;
export default SEARCH;
